/**
 * createAppStore - the one way app state stores are built.
 *
 * Every store is a VANILLA zustand store wrapped in subscribeWithSelector, so
 * scene/entity/input code can take transient subscriptions
 * (`store.subscribe(selector, cb)`) without React anywhere near the render
 * loop. The React side (Phase R) binds with `useStore(store, selector)` from
 * 'zustand' - same store object, no separate React state.
 *
 * Convention: stores expose plain exported action FUNCTIONS (not actions on
 * the state object) so imperative call sites read like the modules they
 * replace (`setPanelOpen('menu', true)`), and the state shape stays a pure
 * data record.
 */
import { createStore } from 'zustand/vanilla';
import type { StoreApi } from 'zustand/vanilla';
import { subscribeWithSelector } from 'zustand/middleware';

/** A store built by {@link createAppStore}: StoreApi plus selector subscribe. */
export type AppStore<T> = ReturnType<typeof createAppStore<T>>;

export function createAppStore<T>(initialState: T) {
  return createStore(subscribeWithSelector<T>(() => initialState));
}

/** Convenience for actions: shallow-patch a store's state. */
export function patchStore<T extends object>(store: StoreApi<T>, patch: Partial<T>): void {
  store.setState(patch);
}
